import React, { useRef, useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import OfferPageGallery from '../OfferPageGallery'
import Pane from 'components/Common/Pane'
import LastMinutePackageOptions from './LastMinutePackageOptions'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import LastMinuteIntro from './LastMinuteIntro'
import config from 'constants/config'
import LastMinuteStickyHeader from './LastMinuteStickyHeader'
import LayoutContainer from 'components/Common/LayoutContainer'
import OfferPageStateProvider from 'contexts/OfferPage/offerPageStateProvider'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useMountedEffect from 'hooks/useMountedEffect'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import Heading from 'LuxKit/Typography/Heading'
import ImmersiveSaveButtonPortal from 'tripPlanner/components/ImmersiveHeader/ImmersiveSaveButtonPortal'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import useOfferPageReducer from 'hooks/OfferPage/useOfferPageReducer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import AlwaysOnAndLMEOfferPageStickyFooter from '../AlwaysOn/AlwaysOnAndLMEOfferPageStickyFooter'
import SavePromptHotel from 'tripPlanner/components/Bookmark/SavePrompt/SavePromptHotel'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import OfferPageSignUpWall from 'components/Common/OfferPageSignUpWall'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'

const RelativeBlock = styled.div`
  position: relative;
`

interface Props {
  offer: App.HotelOffer
}

function LastMinuteOfferContent(props: Props) {
  const intl = useIntl()
  const { offer } = props

  const [state, stateDispatch] = useOfferPageReducer({
    duration: offer.lowestPricePackage?.duration,
    offer,
  })

  const packageOptionsAnchor = useRef<HTMLDivElement>(null)

  const scrollToPackageOptions = useCallback(() => packageOptionsAnchor.current?.scrollIntoView(), [])

  useMountedEffect(() => {
    stateDispatch({ type: OfferPageActions.setOffer, offer })
  }, [offer])

  const availableForPurchase = useIsAvailableForPurchase(state)

  return (
    <OfferPageStateProvider state={state} dispatch={stateDispatch}>
      <ImmersiveSaveButtonPortal>
        <BookmarkButton offer={offer} />
      </ImmersiveSaveButtonPortal>
      <TripGuard>
        <SavePromptHotel offer={offer} />
      </TripGuard>
      <RelativeBlock>
        <div>
          <CSSBreakpoint min="tablet">
            <LastMinuteStickyHeader offer={offer} />
          </CSSBreakpoint>
          <OfferPageGallery offer={offer} autoPlayVideo={!!offer.isVideoVimeoHero} />
          <Pane>
            <LastMinuteIntro />
          </Pane>
        </div>
        <ScrollAnchor ref={packageOptionsAnchor} />
        <Pane padding="20 0" id="Package-Options">
          <LayoutContainer>
            <VerticalSpacer gap={32}>
              <VerticalSpacer gap={16}>
                <Heading variant="heading2" format="titlecase" as="h2" align="center">
                  {intl.formatMessage(
                    {
                      id: 'LN6tk0Fsk_bXYsPWmROx',
                      defaultMessage: '{travelSynonym} Options',
                      description: 'Heading for tour package options section on the tour page',
                    },
                    { travelSynonym: config.brandConfig.travelSynonym },
                  )}
                </Heading>
              </VerticalSpacer>
            </VerticalSpacer>
          </LayoutContainer>
        </Pane>
        {availableForPurchase &&
          <AlwaysOnAndLMEOfferPageStickyFooter
            offer={offer}
            onViewPackageOptions={scrollToPackageOptions}
          />}
        {offer.packages.length > 0 && <LastMinutePackageOptions />}
      </RelativeBlock>
      <SoftOfferPageWalledGarden
        wall={<OfferPageSignUpWall
          offerType="hotel"
        />}
      />
    </OfferPageStateProvider>
  )
}

export default React.memo(LastMinuteOfferContent)
